import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from './auth/AuthProvider'
import { useAuth, usesAdminShell } from './auth/AuthContext'
import { RequireApprover, RequireAuth, RequireRole } from './auth/guards'
import { AppShell } from './components/AppShell'
import { Loading } from './components/ui'
import { LoginPage } from './routes/LoginPage'
import { SetPasswordPage } from './routes/SetPasswordPage'
import { NotFound } from './routes/NotFound'
import { CoachHome } from './routes/coach/CoachHome'
import { CoachEvents } from './routes/coach/CoachEvents'
import { CoachEventDetail } from './routes/coach/CoachEventDetail'
import { CoachTraining } from './routes/coach/CoachTraining'
import { CoachEarnings } from './routes/coach/CoachEarnings'
import { CoachRecord } from './routes/coach/CoachRecord'
import { CoachInbox } from './routes/coach/CoachInbox'
import { OnboardingPage } from './routes/coach/OnboardingPage'
import type { AppRole } from './types/db'

/**
 * The core-team screens are loaded on demand. Twenty two coaches on phones make
 * up almost every session, and they never open them, so there is no reason to
 * ship that code to them.
 */
const AdminDashboard = lazy(() =>
  import('./routes/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
)
const AdminEvents = lazy(() =>
  import('./routes/admin/AdminEvents').then((m) => ({ default: m.AdminEvents })),
)
const AdminEventDetail = lazy(() =>
  import('./routes/admin/AdminEventDetail').then((m) => ({ default: m.AdminEventDetail })),
)
const AdminRoster = lazy(() =>
  import('./routes/admin/AdminRoster').then((m) => ({ default: m.AdminRoster })),
)
const AdminApprovals = lazy(() =>
  import('./routes/admin/AdminApprovals').then((m) => ({ default: m.AdminApprovals })),
)
const AdminChaseList = lazy(() =>
  import('./routes/admin/AdminChaseList').then((m) => ({ default: m.AdminChaseList })),
)
const MaterialsHome = lazy(() =>
  import('./routes/materials/MaterialsHome').then((m) => ({ default: m.MaterialsHome })),
)
const FinanceHome = lazy(() =>
  import('./routes/finance/FinanceHome').then((m) => ({ default: m.FinanceHome })),
)

const CORE_TEAM: AppRole[] = ['admin', 'finance', 'materials']

/** Role guard plus the Suspense boundary the lazy screens need. */
function Only({ roles, children }: { roles: AppRole[]; children: ReactNode }) {
  return (
    <RequireRole roles={roles}>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </RequireRole>
  )
}

/** Approver guard (Michel or a delegated backup, whatever their role) plus Suspense. */
function Approvers({ children }: { children: ReactNode }) {
  return (
    <RequireApprover>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </RequireApprover>
  )
}

/**
 * Routes.
 *
 * Two shells: coaches get a bottom tab bar, the core team gets an admin nav.
 * Role guards keep people out of the wrong shell, but the real enforcement is
 * RLS in Postgres - a coach who types /admin/roster gets an empty table, not a
 * roster.
 */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />

          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            {/* Coach */}
            <Route index element={<RoleHome />} />
            <Route path="events" element={<CoachEvents />} />
            <Route path="events/:eventId" element={<CoachEventDetail />} />
            <Route path="training" element={<CoachTraining />} />
            <Route path="earnings" element={<CoachEarnings />} />
            <Route path="record" element={<CoachRecord />} />
            <Route path="inbox" element={<CoachInbox />} />
            <Route path="setup" element={<OnboardingPage />} />

            {/* Core team. /admin is the shared entry point; Gina and Pascal bounce to their own home. */}
            <Route
              path="admin"
              element={
                <Only roles={CORE_TEAM}>
                  <CoreTeamHome />
                </Only>
              }
            />
            <Route
              path="admin/events"
              element={
                <Only roles={CORE_TEAM}>
                  <AdminEvents />
                </Only>
              }
            />
            <Route
              path="admin/events/:eventId"
              element={
                <Only roles={CORE_TEAM}>
                  <AdminEventDetail />
                </Only>
              }
            />
            <Route
              path="admin/roster"
              element={
                <Only roles={CORE_TEAM}>
                  <AdminRoster />
                </Only>
              }
            />
            <Route
              path="admin/approvals"
              element={
                <Approvers>
                  <AdminApprovals />
                </Approvers>
              }
            />
            <Route
              path="admin/chase"
              element={
                <Only roles={['admin']}>
                  <AdminChaseList />
                </Only>
              }
            />
            <Route
              path="materials"
              element={
                <Only roles={['materials', 'admin']}>
                  <MaterialsHome />
                </Only>
              }
            />
            <Route
              path="finance"
              element={
                <Only roles={['finance', 'admin']}>
                  <FinanceHome />
                </Only>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}

/** The root path means different things to a coach and to the core team. */
function RoleHome() {
  const { role } = useAuth()
  return usesAdminShell(role) ? <Navigate to="/admin" replace /> : <CoachHome />
}

/**
 * /admin is where every core-team login lands (homePathForRole). Michel gets
 * the dashboard; Gina and Pascal get the screen built for their job.
 */
function CoreTeamHome() {
  const { role } = useAuth()
  if (role === 'materials') return <Navigate to="/materials" replace />
  if (role === 'finance') return <Navigate to="/finance" replace />
  return <AdminDashboard />
}
